/**
 * NodeDetailSheet component with Context
 * 改良版ドロワー内部デザイン - Hooksの順序問題修正版
 */

import React, { useCallback, useState } from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from '@/components/ui/sheet';
import { 
  Card, 
  CardContent, 
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import { 
  AlertCircleIcon, 
  ChevronRightIcon, 
  ClockIcon, 
  InfoIcon, 
  LinkIcon,
  MessageSquareIcon,
  RefreshCwIcon,
} from 'lucide-react';
import { TopicNode } from '@/types/nodeTypes';
import { getStatusVariant, getStatusLabel } from '@/types/nodeTypes';
import { getPhaseName, getPhaseColor } from '@/types/phaseTypes';
import { getRelatedNodes } from '@/lib/utils';
import { useMapState, useMapExtraction, useMapUI } from '@/contexts/map';

/**
 * Spinner component
 */
const Spinner = ({ className = 'h-4 w-4' }: { className?: string }) => (
  <div className={`animate-spin rounded-full border-b-2 border-foreground ${className}`} />
);

/**
 * ImportanceIndicator component
 */
const ImportanceIndicator = ({ level, size = 'md' }: { level: number, size?: 'sm' | 'md' | 'lg' }) => {
  const dotSizes = {
    sm: 'w-1 h-1',
    md: 'w-1.5 h-1.5',
    lg: 'w-2 h-2'
  };
  const gaps = {
    sm: 'gap-0.5',
    md: 'gap-1',
    lg: 'gap-1.5'
  };
  
  return (
    <div className={`flex items-center ${gaps[size]}`}>
      {Array.from({ length: 5 }).map((_, i) => (
        <div
          key={i}
          className={`${dotSizes[size]} rounded-full ${i < level ? "bg-foreground" : "bg-foreground/20"}`}
        />
      ))}
    </div>
  );
};

/**
 * ContentItem component for displaying individual content
 */
const ContentItem = ({ content, timestamp }: { content: string, timestamp: string }) => {
  return (
    <div className="py-2 space-y-1.5">
      <div className="text-sm">{content}</div>
      <div className="text-xs text-muted-foreground flex items-center">
        <ClockIcon className="w-3 h-3 mr-1.5" />
        {timestamp}
      </div>
    </div>
  );
};

/**
 * RelatedNodeCard component
 */
const RelatedNodeCard = ({ node, onClick, active = true }: { 
  node: TopicNode; 
  onClick: () => void;
  active?: boolean;
}) => {
  return (
    <Card 
      className={`mb-2 overflow-hidden transition-all ${active ? '' : 'opacity-60'}`}
      onClick={onClick}
    >
      <CardContent className="p-4">
        <div className="flex justify-between items-start">
          <div className="space-y-1">
            <div 
              className="w-2 h-2 rounded-full mb-1"
              style={{ backgroundColor: getPhaseColor(node.phaseId) }}
            />
            <div className="text-sm font-medium leading-tight">{node.title}</div>
            <div className="text-xs text-muted-foreground">
              {getPhaseName(node.phaseId)}
            </div>
          </div>
          <Button 
            size="sm" 
            variant="ghost" 
            className="h-7 w-7 p-0" 
            onClick={(e) => {
              e.stopPropagation();
              onClick();
            }}
          >
            <ChevronRightIcon className="h-4 w-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

/**
 * NodeDetailSheet props
 */
interface NodeDetailSheetProps {
  node: TopicNode | null;
  isOpen: boolean;
  onClose: () => void;
}

/**
 * NodeDetailSheet component
 */
const NodeDetailSheet: React.FC<NodeDetailSheetProps> = ({
  node,
  isOpen,
  onClose,
}) => {
  const { mapData, updateNode } = useMapState();
  const { retryNode } = useMapExtraction();
  const { selectNode } = useMapUI();
  const [activeTab, setActiveTab] = useState('content');
  
  // Hooksはコンポーネントのトップレベルで定義する必要があります
  // これらのfunctionは条件付きで実行されないように定義しています
  const handleRetry = useCallback(async () => {
    if (!node || !mapData) return;
    
    try {
      const updatedNode = await retryNode(node.id, mapData.nodes, mapData.transcript);
      if (updatedNode) {
        updateNode(updatedNode.id, updatedNode);
      }
    } catch (error) {
      console.error('Failed to retry node extraction:', error);
    }
  }, [node, mapData, retryNode, updateNode]);
  
  const handleNodeSelect = useCallback((nodeId: string) => {
    onClose();
    selectNode(nodeId);
  }, [onClose, selectNode]);
  
  // nodeがnullの場合は何も表示しない
  if (!node || !mapData) return null;
  
  const statusVariant = getStatusVariant(node.status);
  const statusLabel = getStatusLabel(node.status);
  const phaseColor = getPhaseColor(node.phaseId, mapData.phases);
  const phaseName = getPhaseName(node.phaseId, mapData.phases);
  const relatedNodes = getRelatedNodes(node.id, mapData.nodes, mapData.edges);
  
  // Get content from contents array if available
  const contentItems = node.contents || [];
  const hasContent = contentItems.length > 0 || (node.content && node.content.trim() !== '');
  
  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent className="w-full max-w-md sm:max-w-lg p-0 flex flex-col">
        <div className="flex-1 overflow-hidden flex flex-col">
          <SheetHeader className="px-6 pt-6 pb-4 border-b">
            <div className="flex items-start justify-between mb-1.5">
              <Badge className="px-2 py-1" style={{ backgroundColor: phaseColor, color: 'white' }}>
                {phaseName}
              </Badge>
              <Badge variant={statusVariant} className="ml-2 px-2 h-6 flex items-center gap-1">
                {node.status === 'processing' && <Spinner className="h-3 w-3 mr-1" />}
                {node.status === 'error' && <AlertCircleIcon className="h-3 w-3 mr-1" />}
                {statusLabel}
              </Badge>
            </div>
            <SheetTitle className="text-xl">{node.title}</SheetTitle>
            <div className="flex items-center justify-between pt-1 text-sm text-muted-foreground">
              <div className="flex items-center gap-2">
                <span className="font-medium">重要度</span>
                <ImportanceIndicator level={node.importance} />
              </div>
              {node.status === 'error' && (
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={handleRetry} 
                  className="h-7 gap-1"
                >
                  <RefreshCwIcon className="h-3 w-3" />
                  再試行
                </Button>
              )}
            </div>
          </SheetHeader>
          
          <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col overflow-hidden p-4">
            <TabsList className="mb-0 mt-0">
              <TabsTrigger value="content" className="flex items-center">
                <MessageSquareIcon className="h-4 w-4 mr-2" />
                内容
              </TabsTrigger>
              <TabsTrigger value="related" className="flex items-center">
                <LinkIcon className="h-4 w-4 mr-2" />
                関連トピック
                {relatedNodes.length > 0 && (
                  <Badge variant="secondary" className="ml-1.5 h-5 min-w-5 px-1">
                    {relatedNodes.length}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="details" className="flex items-center">
                <InfoIcon className="h-4 w-4 mr-2" />
                詳細
              </TabsTrigger>
            </TabsList>
            
            <ScrollArea className="flex-1">
              <TabsContent value="content" className="mt-0">
                <Card>
                  <CardContent className="p-4">
                    {node.status === 'processing' ? (
                      <div className="py-8 flex flex-col items-center justify-center text-muted-foreground">
                        <Spinner className="h-6 w-6 mb-2" />
                        <span>処理中...</span>
                      </div>
                    ) : node.status === 'error' ? (
                      <Alert variant="destructive" className="mb-2">
                        <AlertCircleIcon className="h-4 w-4" />
                        <AlertTitle>エラーが発生しました</AlertTitle>
                        <AlertDescription>
                          {node.error?.message || "不明なエラーです"}
                        </AlertDescription>
                      </Alert>
                    ) : !node.active ? (
                      <div className="py-8 flex flex-col items-center justify-center text-muted-foreground">
                        <p className="text-sm italic">会話内で言及がありませんでした</p>
                      </div>
                    ) : contentItems.length > 0 ? (
                      <div className="space-y-1">
                        {contentItems.map((item, index) => (
                          <React.Fragment key={index}>
                            <ContentItem 
                              content={item.content} 
                              timestamp={item.timestamp} 
                            />
                            {index < contentItems.length - 1 && <Separator />}
                          </React.Fragment>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm">{node.content}</p>
                    )}
                  </CardContent>
                </Card>
                
                {node.active && relatedNodes.length > 0 && (
                  <div className="mt-6">
                    <h3 className="text-sm font-medium mb-2 flex items-center">
                      <LinkIcon className="h-4 w-4 mr-1.5" />
                      関連するトピック
                    </h3>
                    {relatedNodes.slice(0, 2).map((relatedNode) => (
                      <RelatedNodeCard 
                        key={relatedNode.id} 
                        node={relatedNode}
                        active={relatedNode.active}
                        onClick={() => handleNodeSelect(relatedNode.id)}
                      />
                    ))}
                    {relatedNodes.length > 2 && (
                      <Button 
                        variant="ghost" 
                        className="text-xs w-full" 
                        onClick={() => setActiveTab('related')}
                      >
                        すべての関連トピックを見る ({relatedNodes.length})
                      </Button>
                    )}
                  </div>
                )}
              </TabsContent>
              
              <TabsContent value="related" className="mt-0 p-6">
                <h3 className="text-sm font-medium mb-3 flex items-center gap-2">
                  <LinkIcon className="h-4 w-4" />
                  関連するトピック
                  {relatedNodes.length > 0 && (
                    <Badge variant="secondary">{relatedNodes.length}</Badge>
                  )}
                </h3>
                
                {relatedNodes.length === 0 ? (
                  <Card>
                    <CardContent className="p-8 flex flex-col items-center justify-center text-muted-foreground">
                      <p className="text-sm italic">関連するトピックはありません</p>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="space-y-2">
                    {relatedNodes.map((relatedNode) => (
                      <RelatedNodeCard 
                        key={relatedNode.id} 
                        node={relatedNode}
                        active={relatedNode.active}
                        onClick={() => handleNodeSelect(relatedNode.id)}
                      />
                    ))}
                  </div>
                )}
              </TabsContent>
              
              <TabsContent value="details" className="mt-0 p-6">
                <div className="space-y-6">
                  <div>
                    <h3 className="text-sm font-medium mb-2">抽出プロンプト</h3>
                    <Card>
                      <CardContent className="p-4">
                        <p className="text-sm">{node.extractionPrompt}</p>
                      </CardContent>
                    </Card>
                  </div>
                  
                  <div>
                    <h3 className="text-sm font-medium mb-2">ステータス情報</h3>
                    <div className="grid grid-cols-2 gap-3">
                      <Card>
                        <CardContent className="p-3 text-center">
                          <p className="text-muted-foreground text-xs mb-1">アクティブ</p>
                          <p className="font-medium">{node.active ? 'はい' : 'いいえ'}</p>
                        </CardContent>
                      </Card>
                      <Card>
                        <CardContent className="p-3 text-center">
                          <p className="text-muted-foreground text-xs mb-1">言及回数</p>
                          <p className="font-medium">{contentItems.length}</p>
                        </CardContent>
                      </Card>
                      <Card>
                        <CardContent className="p-3 text-center">
                          <p className="text-muted-foreground text-xs mb-1">デフォルト重要度</p>
                          <div className="flex justify-center">
                            <ImportanceIndicator level={node.defaultImportance} size="sm" />
                          </div>
                        </CardContent>
                      </Card>
                      <Card>
                        <CardContent className="p-3 text-center">
                          <p className="text-muted-foreground text-xs mb-1">現在の重要度</p>
                          <div className="flex justify-center">
                            <ImportanceIndicator level={node.importance} size="sm" />
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  </div>
                  
                  <div>
                    <h3 className="text-sm font-medium mb-2">システム情報</h3>
                    <Card>
                      <CardContent className="p-4">
                        <div className="grid grid-cols-[120px_1fr] gap-y-2 text-sm">
                          <span className="text-muted-foreground">ID</span>
                          <span className="font-mono text-xs truncate">{node.id}</span>
                          <span className="text-muted-foreground">フェーズID</span>
                          <span className="font-mono text-xs">{node.phaseId}</span>
                          <span className="text-muted-foreground">ステータス</span>
                          <div className="flex items-center">
                            <Badge variant={statusVariant} className="text-xs">
                              {statusLabel}
                            </Badge>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </div>
              </TabsContent>
            </ScrollArea>
          </Tabs>
        </div>
        
        <SheetFooter className="px-6 py-4 border-t">
          <Button variant="outline" onClick={onClose}>
            閉じる
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};

export default NodeDetailSheet;